import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr'

import { API_BASE, freshAccessToken } from './api'

const TOKEN_KEY = 'ufuqerp_token'
const HUB_PATH = '/hubs/notifications'
/** فشل بداية الاتصال (خادمٌ يُعاد تشغيله، شبكة) — نعاود بهدوء كل 10 ثوانٍ */
const RETRY_MS = 10_000

let connection: HubConnection | null = null
let starting: Promise<void> | null = null
let retryTimer: ReturnType<typeof setTimeout> | null = null
/** المستخدم داخل النظام ويريد الاتصال — يُطفأ بالخروج فلا نعاود المحاولة */
let wanted = false
/** انقطعنا (أو تعذّر الاتصال) منذ آخر اتصالٍ ناجح — العودة تستوجب إعادة مزامنة */
let missed = false

// ─── حالة الاتصال الحيّ — لمؤشر «مباشر» بالشاشات ──────────────────────
export type LiveStatus = 'connected' | 'connecting' | 'offline'

let status: LiveStatus = 'offline'
const statusListeners = new Set<() => void>()

function setStatus(next: LiveStatus) {
  if (status === next) return
  status = next
  statusListeners.forEach((l) => l())
}

export function getLiveStatus(): LiveStatus {
  return status
}

export function subscribeLiveStatus(listener: () => void): () => void {
  statusListeners.add(listener)
  return () => {
    statusListeners.delete(listener)
  }
}

// ─── إعادة المزامنة بعد انقطاع ────────────────────────────────────────
const resyncListeners = new Set<() => void>()

/**
 * يُستدعى حين يعود الاتصال بعد انقطاع: ما بُثّ أثناءه لا يُعاد بثّه، فتعيد الشاشات
 * تحميل بياناتها مرة واحدة ولا يضيع اسمٌ وصل والشبكة مقطوعة.
 */
export function onLiveResync(listener: () => void): () => void {
  resyncListeners.add(listener)
  return () => {
    resyncListeners.delete(listener)
  }
}

function resync() {
  missed = false
  resyncListeners.forEach((l) => {
    try {
      l()
    } catch {
      // شاشةٌ واحدة لا تُسقط البقية
    }
  })
}

/**
 * Lazily create / return the singleton SignalR connection.
 *
 * Auth: every (re)connect asks `freshAccessToken()` — a token that expired while the
 * page stayed open is silently refreshed first, so the live link never sticks on a 401.
 *
 * Reconnect: SignalR retries 5 times (1s, 2s, 5s, 10s, 30s); if the server is still
 * down after that (a long deploy, a dead network) `onclose` keeps retrying every 10s
 * for as long as the user is signed in — a screen never silently stops being live.
 *
 * Idempotent: callers may invoke `getConnection()` repeatedly; only one
 * underlying connection is created.
 */
export function getConnection(): HubConnection {
  if (connection) return connection
  const c = new HubConnectionBuilder()
    .withUrl(`${API_BASE}${HUB_PATH}`, { accessTokenFactory: () => freshAccessToken() })
    .withAutomaticReconnect([1000, 2000, 5000, 10_000, 30_000])
    .configureLogging(import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error)
    .build()
  c.onreconnecting(() => {
    missed = true
    setStatus('connecting')
  })
  c.onreconnected(() => {
    setStatus('connected')
    resync()
  })
  c.onclose(() => {
    if (c !== connection) return // اتصالٌ أُغلق عمداً بالخروج
    missed = true
    setStatus('offline')
    scheduleRetry(2000)
  })
  connection = c
  installWakeListeners()
  return c
}

/**
 * Start the connection if it isn't already running. Safe to call repeatedly.
 *
 * Returns the promise of the in-flight start (or a resolved promise if the
 * connection is already up). Errors are swallowed and logged — SignalR is
 * a best-effort enhancement and must never break a screen.
 */
export function startConnection(): Promise<void> {
  wanted = true
  const c = getConnection()
  if (c.state === HubConnectionState.Connected) return Promise.resolve()
  if (starting) return starting
  // يتصل أو يعيد الاتصال الآن — SignalR يتولاه، والبداية المزدوجة ترمي خطأ
  if (c.state !== HubConnectionState.Disconnected) return Promise.resolve()
  // خارج النظام (صفحة الدخول) — لا اتصال بلا توكن
  if (!localStorage.getItem(TOKEN_KEY)) return Promise.resolve()

  setStatus('connecting')
  starting = c
    .start()
    .then(() => {
      clearRetry()
      setStatus('connected')
      if (missed) resync()
    })
    .catch((err) => {
      console.warn('[signalr] start failed:', err)
      missed = true
      setStatus('offline')
      // withAutomaticReconnect يغطي الانقطاع بعد الاتصال فقط — فشل البداية
      // (مثلاً أثناء إعادة تشغيل الـ API عند النشر) نعيد محاولته كل 10 ثوانٍ.
      scheduleRetry(RETRY_MS)
    })
    .finally(() => {
      starting = null
    })
  return starting
}

/** الخروج من النظام: اتصال المستخدم يُغلق فلا يصل حسابَ من يدخل بعده شيءٌ من إشعاراته */
export async function stopConnection(): Promise<void> {
  wanted = false
  missed = false
  clearRetry()
  const c = connection
  connection = null
  setStatus('offline')
  if (c && c.state !== HubConnectionState.Disconnected) {
    await c.stop().catch(() => {})
  }
}

function scheduleRetry(ms: number) {
  if (!wanted || retryTimer) return
  retryTimer = setTimeout(() => {
    retryTimer = null
    void startConnection()
  }, ms)
}

function clearRetry() {
  if (retryTimer) {
    clearTimeout(retryTimer)
    retryTimer = null
  }
}

/** عودة الشبكة أو عودة الموظف للتبويب: لا ننتظر دورة المحاولة — نتصل فوراً */
let wakeInstalled = false
function installWakeListeners() {
  if (wakeInstalled || typeof window === 'undefined') return
  wakeInstalled = true
  const wake = () => {
    if (!wanted || !connection || connection.state !== HubConnectionState.Disconnected) return
    clearRetry()
    void startConnection()
  }
  window.addEventListener('online', wake)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') wake()
  })
}

// ─── Event names — mirror the backend hub events one-to-one ───────────
//
// When adding a new event, also add the matching `IHubContext` send in the
// backend service (see `MIGRATION_NOTES.md` for the contract).
export type HubEvent =
  | 'AttendanceCheckedIn'
  | 'AttendanceCheckedOut'
  | 'SaleReserved'
  | 'SaleCompleted'
  | 'LeaveRequested'
  | 'LeaveApproved'
  | 'NotificationCreated'
  | 'CrmChanged'

/**
 * مناطق التحديث الحيّ — تطابق `LiveAreas` بالخادم حرفياً. كل شاشة تسمع منطقتها
 * وتعيد تحميل بياناتها فور وصول `CrmChanged` بلا تحديث يدوي.
 */
export type LiveArea =
  | 'media-leads'
  | 'call-tasks'
  | 'booking-queue'
  | 'reception-bookings'
  | 'assigned-customers'
  | 'real-estate-partners'
  | 'partner-listings'
  | 'my-access'
  | 'booking-confirmations'

/** حمولة `CrmChanged` كما يرسلها الخادم (camelCase) */
export interface LiveChange {
  area: LiveArea
  at: string
  data?: Record<string, unknown> | null
}
